import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { mapItems, propertyMappings } from "../../utils/utils";
import { 
  API_BASE_URL, 
  initializeApiToken, 
  getAccessToken, 
  uploadFile, 
  listFiles, 
  deleteFile, 
  cleanChatId 
} from "./api";

const WEBSOCKET_URL = `${process.env.REACT_APP_DOMAIN_WS}/ws/assistant/`;

// Import primaryGoals data for reference in messages
const primaryGoals = [
  { id: 'info', label: 'Richiesta informazioni' },
  { id: 'email', label: 'Rispondere ad una mail' },
  { id: 'lead', label: 'Impostare un primo contatto con un lead' },
  { id: 'social', label: 'Creare un contenuto social' },
  { id: 'summary', label: 'Riassumere una comunicazione' }
];

const VEHICLE_RESULT_TOOLS = new Set(["car_stock_search", "car_configurator"]);

const normalizeToolList = (tool) => {
  if (Array.isArray(tool)) return tool.filter(Boolean);
  if (typeof tool === "string" && tool.trim()) return [tool.trim()];
  return [];
};

const isVehicleResultTool = (tool) => (
  normalizeToolList(tool).some((toolName) => VEHICLE_RESULT_TOOLS.has(toolName))
);

const useChatWebSocket = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Chat ID management
  const [chatId, setChatId] = useState(() => {
    const storedChatId = localStorage.getItem("aitomotivelab_personalData_chat_id");
    return storedChatId && storedChatId !== "undefined" && storedChatId !== "null"
      ? storedChatId
      : null;
  });
  const chatIdRef = useRef(chatId);

  useEffect(() => {
    chatIdRef.current = chatId;
    if (
      chatId &&
      (!localStorage.getItem("aitomotivelab_personalData_chat_id") ||
        localStorage.getItem("aitomotivelab_personalData_chat_id") === "undefined" ||
        localStorage.getItem("aitomotivelab_personalData_chat_id") === "null")
    ) {
      localStorage.setItem("aitomotivelab_personalData_chat_id", chatId);
    }
  }, [chatId]);

  // Basic state management
  const [error, setError] = useState(null);
  const lastUserMessage = useRef("");
  const currentMessageRef = useRef(null);
  const websocketRef = useRef(null);
  const initializeWebSocketRef = useRef(null);
  const isConnectingRef = useRef(false);
  const authTimeoutRef = useRef(null);
  const chatJoinAttempts = useRef(0); // Track attempts to join a chat
  const MAX_JOIN_ATTEMPTS = 2; // Maximum number of attempts before creating a new chat

  // Reconnection handling
  const [isReconnecting, setIsReconnecting] = useState(false);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 3; // Reduced from 5
  const RECONNECT_INTERVAL = 10000; // 10 seconds - less aggressive
  const [reconnectCountdown, setReconnectCountdown] = useState(0);
  
  // History tracking
  const initialLoad = useRef(true);

  // Create a buffer for incoming stream messages to batch updates
  const messageBuffer = useRef({ text: "", active: false });
  const messageUpdateTimer = useRef(null);
  
  // Function to format message text (moved to a shared function)
  const formatMessageText = useCallback((text) => {
    if (!text) return "";
    const paragraphs = text.split('\n').filter(p => p.trim());
    return paragraphs
      .map(p => p.trim())
      .join('\n\n');
  }, []);

  // Function to process buffered messages in batches
  const processMessageBuffer = useCallback(() => {
    if (messageBuffer.current.active && messageBuffer.current.text.length > 0) {
      setMessages((prevMessages) => {
        // If no previous messages or buffer is empty, return original state
        if (prevMessages.length === 0 || messageBuffer.current.text.length === 0) {
          return prevMessages;
        }

        const lastMessage = prevMessages[prevMessages.length - 1];
        
        // Optimize: If the last message is the server message being streamed into,
        // update its text property directly without creating a new array reference.
        if (lastMessage?.sender === "server" && lastMessage.loading) {
          const updatedText = formatMessageText(
            (currentMessageRef.current?.text || lastMessage.text || "") + messageBuffer.current.text
          );

          // Update the current message reference used internally
          if (currentMessageRef.current) {
            currentMessageRef.current.text = updatedText;
          }
          
          // Update the text of the last message object directly
          lastMessage.text = updatedText;

          // Clear the buffer
          messageBuffer.current.text = "";

          // Return the *same* array reference, but React will detect the mutation
          // within the updater function and re-render necessary components.
          // Note: Mutating state directly is generally discouraged, but acceptable 
          // inside the setState updater function for performance optimizations like this.
          // However, to be safer and ensure React detects the change reliably, 
          // we return a *new* array but reuse unchanged message objects.
          const newMessages = [...prevMessages]; // Still create a new top-level array
          newMessages[newMessages.length - 1] = { ...lastMessage }; // Create a new object for the last message
          return newMessages; 
          
        } else {
          // If the last message isn't a loading server message, 
          // it means we need to add a *new* server message.
          const newMessage = {
            sender: "server",
            text: formatMessageText(messageBuffer.current.text),
            tool: [],
            loading: true,
          };
          currentMessageRef.current = newMessage;
          
          // Clear the buffer
          messageBuffer.current.text = "";
          
          // Return a new array with the new message added
          return [...prevMessages, newMessage];
        }
      });
    }
  }, [formatMessageText]);
  
  // Set up interval for processing buffered messages
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (messageBuffer.current.active) {
        processMessageBuffer();
      }
    }, 50); // Update UI at most every 50ms
    
    return () => clearInterval(intervalId);
  }, [processMessageBuffer]);

  // Replace initializeApi with direct call to the imported function
  const initializeApi = async () => {
    // Initialize API token directly
    await initializeApiToken();
  };

  // Reconnection logic
  const attemptReconnect = useCallback(() => {
    if (isReconnecting) return;

    setIsReconnecting(true);
    reconnectAttempts.current += 1;

    if (reconnectAttempts.current <= MAX_RECONNECT_ATTEMPTS) {
      // Exponential backoff: base interval * 2^(attempts-1), capped at 2 minutes
      const delay = Math.min(
        RECONNECT_INTERVAL * Math.pow(2, reconnectAttempts.current - 1),
        120000
      );
      setReconnectCountdown(Math.ceil(delay / 1000));
      //console.log(`Attempting to reconnect (${reconnectAttempts.current}/${MAX_RECONNECT_ATTEMPTS}) in ${Math.ceil(delay / 1000)}s`);

      reconnectTimeoutRef.current = setTimeout(() => {
        if (initializeWebSocketRef.current) {
          initializeWebSocketRef.current();
        }
      }, delay);
    } else {
      //console.log("Max reconnection attempts reached");
      setError("Failed to reconnect after multiple attempts. Please try again manually.");
      setIsReconnecting(false);
    }
  }, [isReconnecting]);

  // Add a connection status monitor that checks periodically
  useEffect(() => {
    // Only run this effect if we should be connected
    if (!isConnected && !isReconnecting && reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
      // Check connection status every 10 seconds
      const connectionMonitor = setInterval(() => {
        if (websocketRef.current?.readyState !== WebSocket.OPEN) {
          //console.log("Connection monitor detected WebSocket is not connected");
          // Only attempt reconnect if we're not already trying and haven't hit the max
          if (!isReconnecting && reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
            setError("Connection lost. Attempting to reconnect...");
            attemptReconnect();
          }
        }
      }, 10000);

      return () => clearInterval(connectionMonitor);
    }
  }, [isConnected, isReconnecting, reconnectAttempts, attemptReconnect, MAX_RECONNECT_ATTEMPTS]);

  // Add a ping-pong mechanism to keep connection alive
  useEffect(() => {
    if (isConnected && websocketRef.current?.readyState === WebSocket.OPEN) {
      // Send a ping message every 30 seconds to keep the connection alive
      const pingInterval = setInterval(() => {
        if (websocketRef.current?.readyState === WebSocket.OPEN) {
          try {
            safelySendMessage(websocketRef.current, { type: "ping" });
          } catch (error) {
            console.error("Error sending ping:", error);
          }
        }
      }, 30000);

      return () => clearInterval(pingInterval);
    }
  }, [isConnected]);

  // Add a helper to safely send WebSocket messages
  const safelySendMessage = (ws, message) => {
    try {
      if (ws && ws.readyState === WebSocket.OPEN) {
        if (typeof message === 'object') {
          ws.send(JSON.stringify(message));
        } else {
          ws.send(message);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error sending WebSocket message:", error);
      return false;
    }
  };

  // WebSocket initialization
  const initializeWebSocket = useCallback(async () => {
    // Prevent concurrent connection attempts
    if (isConnectingRef.current) {
      return;
    }

    if (websocketRef.current) {
      try {
        websocketRef.current.close();
      } catch (error) {
        console.error("Error closing existing WebSocket:", error);
      }
    }

    try {
      isConnectingRef.current = true;
      await initializeApiToken();
      const token = await getAccessToken();
      
      // Get API token from localStorage
      const storedApiToken = localStorage.getItem("api_token") || "default_api_token";

      // Add a timestamp parameter to avoid caching issues
      const wsUrl = `${WEBSOCKET_URL}?t=${Date.now()}`;
      const ws = new WebSocket(wsUrl);
      websocketRef.current = ws;

      ws.onopen = () => {
        //console.log("WebSocket connection opened");
        setIsConnected(true);
        setIsReconnecting(false);
        isConnectingRef.current = false;
        reconnectAttempts.current = 0;
        setError(null);
        
        // Send authentication with JWT token
        safelySendMessage(ws, {
          type: "authenticate",
          token: token,
          api_token: storedApiToken,
          fullUrl: window.location.href,
        });

        // Start authentication timeout guard
        if (authTimeoutRef.current) clearTimeout(authTimeoutRef.current);
        authTimeoutRef.current = setTimeout(() => {
          if (!isAuthenticated && websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
            setError("Authentication timeout. Reconnecting...");
            try {
              websocketRef.current.close(4000, "Auth timeout");
            } catch {}
            if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
              attemptReconnect();
            }
          }
        }, 8000);
      };

      ws.onmessage = handleWebSocketMessage;

      ws.onclose = (event) => {
        //console.log("WebSocket connection closed with code:", event.code);
        setIsConnected(false);
        setIsAuthenticated(false);
        isConnectingRef.current = false;
        if (authTimeoutRef.current) {
          clearTimeout(authTimeoutRef.current);
          authTimeoutRef.current = null;
        }
        
        // Don't attempt to reconnect on normal close
        if (event.code !== 1000) {
          setError("WebSocket disconnected. Attempting to reconnect...");
          if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
            attemptReconnect();
          } else {
            setError("Connection failed. Please try reconnecting manually.");
          }
        }
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        setIsConnected(false);
        isConnectingRef.current = false;
        if (authTimeoutRef.current) {
          clearTimeout(authTimeoutRef.current);
          authTimeoutRef.current = null;
        }
        if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
          setError("WebSocket connection error. Attempting to reconnect...");
          attemptReconnect();
        } else {
          setError("Connection error. Please try reconnecting manually.");
        }
      };
    } catch (error) {
      console.error("Error initializing WebSocket:", error);
      setError(error.message || "Failed to initialize chat");
      isConnectingRef.current = false;
      attemptReconnect();
    }
  }, [attemptReconnect]);

  // Cleanup function
  const cleanup = useCallback(() => {
    //console.log("Cleaning up WebSocket and chat state");
    if (websocketRef.current) {
      // Try to close the connection properly
      try {
        // Only send a leave_chat message if we have an active chat
        if (chatIdRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
          safelySendMessage(websocketRef.current, {
            type: "leave_chat",
            chat_id: chatIdRef.current
          });
        }
        websocketRef.current.close(1000, "Normal closure");
      } catch (error) {
        console.error("Error during WebSocket cleanup:", error);
      }
      websocketRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    setMessages([]);
    setIsConnected(false);
    setIsAuthenticated(false);
    setError(null);
    setIsReconnecting(false);
    reconnectAttempts.current = 0;
    // Don't reset chatId here as it might be needed for rejoining
  }, []);

  // Countdown timer for reconnection
  useEffect(() => {
    let countdownInterval;
    if (reconnectCountdown > 0) {
      countdownInterval = setInterval(() => {
        setReconnectCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (countdownInterval) {
        clearInterval(countdownInterval);
      }
    };
  }, [reconnectCountdown]);

  const handleManualReconnect = () => {
    reconnectAttempts.current = 0;
    setError(null);
    setIsReconnecting(true);
    
    // Clear any existing timeouts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    
    // Add a small delay before reconnecting
    reconnectTimeoutRef.current = setTimeout(() => {
      if (initializeWebSocketRef.current) {
        initializeWebSocketRef.current();
      }
      setIsReconnecting(false);
    }, 500);
  };

  // WebSocket message handler
  const handleWebSocketMessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      
      // Add detailed debug logging for all message types
      //console.log(`[WebSocket] Received message type: ${data.type}`);

      // Handle stream messages via dedicated function
      if (data.type === "stream_message") {
        processStreamMessage(data);
        return;
      }

      // Early check for documentation text which shouldn't be displayed to users
      if (data.type === "message_complete") {
        // Filter out the Car Insights Visualization Guide documentation
        if (data.message && typeof data.message === 'string' && 
            data.message.includes('# Car Insights Visualization Guide')) {
          //console.log('[WebSocket] Detected documentation text in message, filtering it out');
          data.message = 'Bentornato all\'Assistente Interno. Come posso aiutarti oggi?';
        }
      }
      
      if (data.type === "stream_message" || data.type === "message_complete") {
        // Log the complete message data structure for debugging
        //console.log(`[WebSocket] Full message data for ${data.type}:`, JSON.stringify(data).substring(0, 500) + "...");
        
        // Check specifically for JSON content in messages
        if (data.message && typeof data.message === 'string') {
          //console.log(`[WebSocket] Message content (first 200 chars): ${data.message.substring(0, 200)}...`);
          
          // Check for JSON patterns in the message
          if (data.message.includes('{') && data.message.includes('}')) {
            //console.log('[WebSocket] Message appears to contain JSON content');
            
            // Check for specific car insights indicators
            if (data.message.includes('chart_data') || 
                data.message.includes('descriptive_information') || 
                data.message.includes('analysis_metadata')) {
              //console.log('[WebSocket] Message likely contains car insights data');
            }
          }
        }
        
        // Log tools explicitly
        if (data.tool) {
          //console.log(`[WebSocket] Message tools:`, data.tool);
        }
        
        // Check for sources/carouselItems
        if (data.sources) {
          //console.log(`[WebSocket] Message has sources:`, 
          //  Array.isArray(data.sources) ? `Array with ${data.sources.length} items` : typeof data.sources);
        }
      }

      if (data.type === "authentication") {
        setIsAuthenticated(data.success);
        // Clear auth timeout on any authentication response
        if (authTimeoutRef.current) {
          clearTimeout(authTimeoutRef.current);
          authTimeoutRef.current = null;
        }
        if (!data.success) {
          setError("Authentication failed. Please refresh the page.");
        } else {
          //console.log("Authentication successful");
          
          // After successful authentication, either join existing chat or create new one
          const storedChatId = localStorage.getItem("aitomotivelab_personalData_chat_id");
          
          // If we have a valid chat ID in localStorage, try to join it
          if (storedChatId && storedChatId !== "undefined" && storedChatId !== "null") {
            // Clean the chat ID before using it
            const cleanedChatId = cleanChatId(storedChatId);
            //console.log("Joining existing chat on authentication:", cleanedChatId);
            
            // Update reference immediately to avoid race conditions
            chatIdRef.current = cleanedChatId;
            setChatId(cleanedChatId);
            
            if (event.target && event.target.readyState === WebSocket.OPEN) {
              // Send join_chat message with the stored ID
              //console.log("Sending join_chat with ID:", cleanedChatId);
              
              // Using setTimeout to ensure the authentication is fully processed
              setTimeout(() => {
                if (event.target?.readyState === WebSocket.OPEN) {
                  safelySendMessage(event.target, {
                    type: "join_chat",
                    chat_id: cleanedChatId
                  });
                }
              }, 100);
              
              // Fetch user chats after a delay
              setTimeout(() => {
                if (event.target?.readyState === WebSocket.OPEN) {
                  safelySendMessage(event.target, {
                    type: "fetch_user_chats",
                    page: 1,
                    page_size: 10
                  });
                }
              }, 500);
            } else {
              setError("WebSocket is not connected. Please try again.");
            }
          } else {
            //console.log("Creating new chat as no stored chat ID found");
            // Clear chatIdRef since we're creating a new chat
            chatIdRef.current = null;
            setChatId(null);
            
            if (event.target && event.target.readyState === WebSocket.OPEN) {
              safelySendMessage(event.target, {
                type: "create_chat"
              });
              
              // After authentication, fetch user chats for history panel
              setTimeout(() => {
                if (event.target?.readyState === WebSocket.OPEN) {
                  safelySendMessage(event.target, {
                    type: "fetch_user_chats",
                    page: 1,
                    page_size: 10
                  });
                }
              }, 500);
            } else {
              setError("WebSocket is not connected. Please try again.");
            }
          }
        }
      } else if (data.type === "chat_created") {
        const cleanedChatId = cleanChatId(data.chat_id);
        setChatId(cleanedChatId);
        chatIdRef.current = cleanedChatId; // Ensure ref is also updated
        localStorage.setItem("aitomotivelab_personalData_chat_id", cleanedChatId);
        chatJoinAttempts.current = 0; // Reset join attempts counter when a new chat is created
        initialLoad.current = true;
        setMessages([]);
        //console.log("New chat created with ID:", data.chat_id, "cleaned to:", cleanedChatId);
        fetchChatHistory();
        // Refresh chat list so the new chat appears immediately
        if (websocketRef.current?.readyState === WebSocket.OPEN) {
          safelySendMessage(websocketRef.current, {
            type: "fetch_user_chats",
            page: 1,
            page_size: 10
          });
        }
      } else if (data.type === "chat_joined") {
        // Always update the chatId when we successfully join a chat
        const cleanedChatId = cleanChatId(data.chat_id);
        setChatId(cleanedChatId);
        // Update chatIdRef for immediate reference
        chatIdRef.current = cleanedChatId;
        // Reset join attempts counter on success
        chatJoinAttempts.current = 0;
        // Only update localStorage if it differs to avoid unnecessary writes
        const storedChatId = localStorage.getItem("aitomotivelab_personalData_chat_id");
        if (storedChatId !== cleanedChatId) {
          localStorage.setItem("aitomotivelab_personalData_chat_id", cleanedChatId);
        }
        //console.log("Joined existing chat with ID:", data.chat_id, "cleaned to:", cleanedChatId);
        fetchChatHistory();
      } else if (data.type === "error" && data.message === "Chat not found") {
        // Get the latest requested chat ID from localStorage
        const latestChatId = localStorage.getItem("aitomotivelab_personalData_chat_id");
        const cleanedLatestChatId = cleanChatId(latestChatId);
        //console.log("Chat not found. chatIdRef.current:", chatIdRef.current);
        //console.log("Latest from localStorage:", latestChatId, "cleaned to:", cleanedLatestChatId);
        
        // Increment join attempt counter to prevent infinite loops
        chatJoinAttempts.current++;
        //console.log(`Chat join attempt ${chatJoinAttempts.current} of ${MAX_JOIN_ATTEMPTS}`);
        
        // If we've tried too many times, just create a new chat
        if (chatJoinAttempts.current >= MAX_JOIN_ATTEMPTS) {
          //console.log(`Exceeded maximum join attempts. Creating new chat.`);
          localStorage.removeItem("aitomotivelab_personalData_chat_id");
          setChatId(null);
          chatIdRef.current = null;
          chatJoinAttempts.current = 0; // Reset counter
          
          if (event.target && event.target.readyState === WebSocket.OPEN) {
            safelySendMessage(event.target, {
              type: "create_chat"
            });
          } else {
            setError("WebSocket is not connected. Please try again.");
          }
          return;
        }
        
        // In case of "Chat not found", we always want to try with the latest requested ID
        // This fixes cases where chatIdRef hasn't been updated yet but localStorage has
        if (cleanedLatestChatId && cleanedLatestChatId !== "undefined" && cleanedLatestChatId !== "null") {
          // Try to join with the latest localStorage ID regardless of what's in chatIdRef
          //console.log("Attempting to join with cleaned chat ID:", cleanedLatestChatId);
          chatIdRef.current = cleanedLatestChatId; // Update the ref to match localStorage
          
          if (event.target && event.target.readyState === WebSocket.OPEN) {
            //console.log("Sending join_chat request with ID:", cleanedLatestChatId);
            safelySendMessage(event.target, {
              type: "join_chat",
              chat_id: cleanedLatestChatId
            });
          } else {
            setError("WebSocket is not connected. Please try again.");
          }
        } else {
          // Only create a new chat if we really don't have a chat ID to try
          //console.log("No valid chat ID found in localStorage, creating new chat");
          chatJoinAttempts.current = 0; // Reset counter
          localStorage.removeItem("aitomotivelab_personalData_chat_id");
          setChatId(null);
          chatIdRef.current = null;
          
          if (event.target && event.target.readyState === WebSocket.OPEN) {
            safelySendMessage(event.target, {
              type: "create_chat"
            });
          } else {
            setError("WebSocket is not connected. Please try again.");
          }
        }
      } else if (data.type === "user_chats") {
        // The ChatHistoryPanel component will handle this message directly
        // No action needed here, as we're passing the websocket to ChatHistoryPanel
      } else if (data.type === "chat_updated" || data.type === "chat_deleted") {
        // These responses confirm successful operations
        // The ChatHistoryPanel will handle updating the UI
        if (websocketRef.current?.readyState === WebSocket.OPEN) {
          safelySendMessage(websocketRef.current, {
            type: "fetch_user_chats",
            page: 1,
            page_size: 10
          });
        }
      } else if (
        data.type === "stream_message" ||
        data.type === "message_complete" ||
        data.type === "admin_message"
      ) {
        handleIncomingMessage(data);
      } else if (data.type === "chat_history") {
        try {
          const parsedHistory = JSON.parse(data.chat_history);
          const formattedHistory = parsedHistory.flatMap((item) =>
            formatChatHistoryItem(item.fields, item.pk)
          );
          
          // Only set messages from history if we don't have existing messages
          // or if this is a fresh chat (first load)
          setMessages(prevMessages => {
            if (prevMessages.length > 0 && !initialLoad.current) {
              return prevMessages;
            }
            initialLoad.current = false;
            return formattedHistory;
          });
        } catch (parseError) {
          setError("Invalid chat history format");
        }
      } else if (data.type === "ping") {
        // Respond with a pong message
        if (event.target && event.target.readyState === WebSocket.OPEN) {
          safelySendMessage(event.target, { type: "pong" });
        }
      } else if (data.type === "pong") {
        // Connection is alive, reset any error states
        if (error && error.includes("connection")) {
          setError(null);
        }
      } else if (data.type === 'file_processed') {
        //console.log('Received file processed event:', data);
        
        // Update file status in our state
        const fileId = data.file_id;
        const newStatus = data.status || 'upload_complete';
        
        setUploadedFiles(prev => prev.map(file => 
          (file.id === fileId || file.file_id === fileId) 
            ? { ...file, process_status: newStatus } 
            : file
        ));
        
        // Stop polling for this file if we're polling it
        if (filePollingRefs.current[fileId]) {
          clearInterval(filePollingRefs.current[fileId]);
          delete filePollingRefs.current[fileId];
        }
        
        // Add a system message about the processed file
        setMessages(prev => [...prev, {
          id: `system-file-processed-${Date.now()}`,
          sender: 'server',
          text: `File "${data.filename || 'Uploaded file'}" has been processed and is now available for the assistant.`,
          timestamp: new Date().toISOString(),
          is_system_message: true
        }]);
      } else if (data.type === 'file_error') {
        //console.log('Received file error event:', data);
        
        // Update file status to error
        const fileId = data.file_id;
        
        setUploadedFiles(prev => prev.map(file => 
          (file.id === fileId || file.file_id === fileId) 
            ? { ...file, process_status: 'error' } 
            : file
        ));
        
        // Stop polling for this file
        if (filePollingRefs.current[fileId]) {
          clearInterval(filePollingRefs.current[fileId]);
          delete filePollingRefs.current[fileId];
        }
        
        // Add an error message
        setMessages(prev => [...prev, {
          id: `system-file-error-${Date.now()}`,
          sender: 'server',
          text: `Error processing file "${data.filename || 'Uploaded file'}". The file content may not be available to the assistant.`,
          timestamp: new Date().toISOString(),
          is_system_message: true,
          error: true
        }]);
      }
    } catch (error) {
      console.error("Error handling WebSocket message:", error);
      setError("Failed to handle WebSocket message");
    }
  };

  // Fetch chat history
  const fetchChatHistory = () => {
    if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
      websocketRef.current.send(
        JSON.stringify({
          type: "fetch_history",
        })
      );
    } else {
      setError("Failed to fetch chat history. Please try again.");
    }
  };

  // Format chat history items
  const formatChatHistoryItem = (message, id) => {
    const formattedMessages = [];
    if (message.sender_message !== null) {
      formattedMessages.push({
        sender: "user",
        text: message.sender_message,
        id,
        file_url: message.sender_file_url || null,
      });
    } else if (message.sender_file_url) {
      formattedMessages.push({
        sender: "user",
        text: "File inviato",
        id,
        file_url: message.sender_file_url,
      });
    }

    if (message.bot_message || isVehicleResultTool(message.tool)) {
      let formattedMessage = {
        sender: "server",
        tool: message.tool || null,
        text: message.bot_message || "",
        id,
        file_url: message.bot_file_url || null,
      };

      // Use raw sources for car_stock tool in history as well
      if (message.sources && isVehicleResultTool(message.tool)) {
        // Assign raw sources directly to carouselItems
        formattedMessage.carouselItems = message.sources; 
      } else if (message.sources) {
          // Handle other sources in history if necessary (potentially map them)
          // Check if mapping exists based on tool
          if (message.tool && propertyMappings[message.tool]) {
              try {
                  formattedMessage.carouselItems = mapItems(message.sources, propertyMappings[message.tool]);
              } catch (error) {
                  console.warn("Error processing carousel items in history:", error);
              }
          } else {
              // Default: assign raw sources if no tool or mapping
              formattedMessage.carouselItems = message.sources; 
          }
      }

      formattedMessages.push(formattedMessage);
    }

    return formattedMessages;
  };

  // Handle incoming messages (streaming or complete)
  const handleIncomingMessage = useCallback((data) => {
    if (data.type === "stream_message") {
      // For tool messages, update immediately but efficiently
      if (data.status === "tool") {
        setMessages((prevMessages) => {
          if (prevMessages.length === 0) return prevMessages; // Should not happen here, but safe check

          const lastMessage = prevMessages[prevMessages.length - 1];
          const toolName = data.message;

          // If last message is a loading server message, update its tools
          if (lastMessage?.sender === "server" && lastMessage.loading) {
            const currentTools = Array.isArray(lastMessage.tool) ? lastMessage.tool : [];
            // Avoid adding duplicate tool names
            const updatedToolList = currentTools.includes(toolName) ? currentTools : [...currentTools, toolName];

            // Only update if tools actually changed
            if (updatedToolList.length > currentTools.length) {
              lastMessage.tool = updatedToolList;
              // Return new array with updated last message object
              const newMessages = [...prevMessages];
              newMessages[newMessages.length - 1] = { ...lastMessage }; 
              return newMessages;
            }
            // If tool already exists, no state update needed
            return prevMessages; 
          } else {
            // Need to add a new server message if last one wasn't a loading server message
            const newMessage = {
              sender: "server",
              tool: [toolName],
              loading: true,
              status: data.status,
              text: "", // Start with empty text
            };
            currentMessageRef.current = newMessage; // Set ref for text streaming
            return [...prevMessages, newMessage];
          }
        });
      } else { // Regular text stream
        // Buffer the updates for processing by processMessageBuffer
        if (!messageBuffer.current.active) {
          messageBuffer.current.active = true;
          messageBuffer.current.text = data.message;
          
          // Force an immediate update check for the first message chunk
          // This ensures the new server message bubble appears promptly
          processMessageBuffer(); 
        } else {
          // Append to buffer for subsequent batched updates
          messageBuffer.current.text += data.message;
        }
      }
    } else if (data.type === "message_complete" || data.type === "admin_message") {
      // Stop buffering and clear buffer
      messageBuffer.current.active = false;
      messageBuffer.current.text = ""; 
      
      setIsLoading(false);
      
      setMessages((prevMessages) => {
        if (prevMessages.length === 0 && data.type !== "admin_message") {
            // Handle case where completion arrives but no prior stream message existed
            // (e.g., direct response without streaming)
            const newMessage = {
                sender: "server",
                text: formatMessageText(data.message),
                loading: false,
                tool: data.tool || [],
                id: data.id,
                metadata: data.metadata ? { ...data.metadata, timestamp: new Date().toISOString() } : undefined,
                carouselItems: undefined // Initialize carouselItems
            };
            // Handle sources and carousel items
            if (data.sources && Array.isArray(data.sources)) {
                // For car_stock_search, pass the raw sources directly
                if (isVehicleResultTool(data.tool)) {
                    newMessage.carouselItems = data.sources; // Use raw sources
                } else {
                    // Handle other potential tools/sources if needed (maybe map simple lists?)
                    // If there's a mapping for other tools, use it, otherwise assign raw
                    if (data.tool && propertyMappings[data.tool]) {
                        try {
                            newMessage.carouselItems = mapItems(data.sources, propertyMappings[data.tool]);
                        } catch (mappingError) {
                            console.warn("Error mapping items:", mappingError);
                        }
                    } else {
                        // Default to raw sources if no specific mapping exists
                        newMessage.carouselItems = data.sources;
                    }
                }
            }
            return [newMessage];
        }

        const newMessages = [...prevMessages];
        const lastMessageIndex = newMessages.length - 1;
        const lastMessage = newMessages[lastMessageIndex];
        
        // Format the complete message text
        const formattedText = formatMessageText(data.message);
        
        // Prepare the final message object
        const finalMessage = {
          sender: "server",
          text: formattedText,
          loading: false, // Mark as complete
          status: undefined,
          tool: data.tool || (lastMessage?.sender === "server" ? lastMessage.tool : []) || [], // Preserve tools if available
          id: data.id || lastMessage?.id, // Use existing ID if available
          metadata: data.metadata ? {
            ...data.metadata,
            timestamp: new Date().toISOString(),
          } : undefined,
          carouselItems: undefined // Initialize carouselItems
        };

        // Handle sources and carousel items
        if (data.sources && Array.isArray(data.sources)) {
          // For car_stock_search, pass the raw sources directly
          if (isVehicleResultTool(data.tool)) {
              finalMessage.carouselItems = data.sources; // Use raw sources
          } else {
            // Handle other potential tools/sources if needed (maybe map simple lists?)
            // If there's a mapping for other tools, use it, otherwise assign raw
             if (data.tool && propertyMappings[data.tool]) {
                  try {
                      finalMessage.carouselItems = mapItems(data.sources, propertyMappings[data.tool]);
                  } catch (mappingError) {
                      console.warn("Error mapping items:", mappingError);
                  }
              } else {
                  // Default to raw sources if no specific mapping exists
                  finalMessage.carouselItems = data.sources;
              }
          }
        }

        // Update the last message if it's the corresponding server response,
        // otherwise add as a new message (e.g., admin message).
        if (lastMessage?.sender === "server" && data.type !== "admin_message") {
          newMessages[lastMessageIndex] = finalMessage; 
        } else {
          newMessages.push(finalMessage);
        }
        
        currentMessageRef.current = null; // Clear current message reference
        
        return newMessages;
      });
    }
  }, [processMessageBuffer, formatMessageText, setIsLoading]); // Added setIsLoading dependency

  // Send message to server
  const handleSend = useCallback(async (input, file = null, url = null, type = "chat_message", metadata = null) => {
    // Check if message is empty or WebSocket not ready
    if ((!input && type !== "structured_prompt") || !websocketRef.current) return;

    setIsLoading(true);
    
    // Store last user message for possible resending
    if (type === "chat_message") {
      lastUserMessage.current = input;
    }

    try {
      // Add user message to UI immediately
      if (type === "chat_message" || type === "structured_prompt") {
        // For structured prompts, create a simplified display message
        let displayText = input;
        if (type === "structured_prompt") {
          const primaryGoalLabel = metadata?.primaryGoal ? 
            (primaryGoals.find(goal => goal.id === metadata.primaryGoal)?.label || metadata.primaryGoal) : '';
          displayText = `Ho selezionato l'obiettivo: ${primaryGoalLabel} - ${metadata.secondaryGoal}`;
        }

        setMessages(prevMessages => [
          ...prevMessages,
          {
            sender: "user",
            text: displayText,
            id: `user-${Date.now()}`,
            timestamp: new Date().toISOString(),
            metadata
          }
        ]);
      }

      // Get the stored chat ID and clean it
      const storedChatId = localStorage.getItem("aitomotivelab_personalData_chat_id") || "";
      const cleanedChatId = cleanChatId(storedChatId);

      // Prepare the message to send to server
      let message;
      
      if (type === "structured_prompt") {
        message = {
          type: "structured_prompt",
          input: input,
          structured_prompt: {
            primaryGoal: metadata.primaryGoal,
            secondaryGoal: metadata.secondaryGoal, 
            selectedCars: metadata.selectedCars || [],
            selectedClients: metadata.selectedClients || [],
            promptText: input
          },
          personaldata: {
            api_token: localStorage.getItem("api_token") || "default_api_token",
            chat_id: cleanedChatId
          }
        };
      } else {
        message = {
          type,
          input,
          personaldata: {
            api_token: localStorage.getItem("api_token") || "default_api_token",
            chat_id: cleanedChatId
          }
        };
      }

      // Send the message using our safe function
      const sent = safelySendMessage(websocketRef.current, message);
      
      if (!sent) {
        setError("Failed to send message. WebSocket connection lost.");
        setIsLoading(false);
        
        // Try to reconnect
        if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
          attemptReconnect();
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setError("Failed to send message");
      setIsLoading(false);
    }
  }, []);

  // Resend last message
  const handleResendLastMessage = () => {
    if (lastUserMessage.current) {
      handleSend(lastUserMessage.current);
    }
  };

  // Store reference to initializeWebSocket function
  useEffect(() => {
    initializeWebSocketRef.current = initializeWebSocket;
  }, [initializeWebSocket]);

  // Process streaming messages (helper function)
  const processStreamMessage = useCallback((data) => {
    if (data.message) {
      // Filter out documentation text that could be streamed
      if (data.message.includes('# Car Insights Visualization Guide')) {
        //console.log('[WebSocket] Detected documentation text in stream, filtering out');
        return; // Skip this stream chunk entirely
      }
      
      messageBuffer.current.active = true;
      messageBuffer.current.text += data.message;

      // Stream processing is now handled by interval
      // processMessageBuffer is called periodically to update UI
    }
  }, []);

  // Add to existing state variables
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [fileUploadProgress, setFileUploadProgress] = useState(null);

  // Add file handling to the returned object
  const handleFileUpload = async (file) => {
    if (!file || !chatId) return;
    
    try {
      setFileUploadProgress({
        filename: file.name,
        progress: 0,
        status: 'uploading'
      });
      
      // Upload the file using the imported uploadFile function
      const uploadedFile = await uploadFile(
        file, 
        chatId, 
        (progress) => setFileUploadProgress(prev => ({
          ...prev,
          ...progress,
          filename: file.name
        }))
      );
      //console.log("File upload complete:", uploadedFile);
      
      // Start a polling mechanism to check processing status
      if (uploadedFile) {
        startFileStatusPolling(uploadedFile.id);
      }
      
      // Add file to state with initial pending status
      setUploadedFiles(prev => [...prev, {
        ...uploadedFile,
        process_status: uploadedFile.process_status || 'pending'
      }]);
      
      // Create a system message to inform about the file being processed
      const fileMessage = {
        id: `system-file-${Date.now()}`,
        sender: 'server',
        text: `File uploaded: "${file.name}" is being processed and added to the conversation context.`,
        timestamp: new Date().toISOString(),
        is_system_message: true
      };
      
      setMessages(prev => [...prev, fileMessage]);
      
      // Reset progress after showing 100%
      setFileUploadProgress({
        filename: file.name,
        progress: 100,
        status: 'upload_complete'
      });
      
      setTimeout(() => {
        setFileUploadProgress(null);
      }, 1000);
      
      // Send websocket message to notify about the uploaded file
      if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
        const message = {
          type: 'file_uploaded',
          chat_id: chatId,
          file_id: uploadedFile.id
        };
        websocketRef.current.send(JSON.stringify(message));
      }
      
      return uploadedFile;
    } catch (error) {
      console.error('Error uploading file:', error);
      setFileUploadProgress({
        filename: file.name,
        progress: 0,
        status: 'error',
        error: error.message
      });
      
      // Add error message
      const errorMessage = {
        id: `system-error-${Date.now()}`,
        sender: 'server',
        text: `Error uploading file "${file.name}": ${error.message}`,
        timestamp: new Date().toISOString(),
        is_system_message: true,
        error: true
      };
      
      setMessages(prev => [...prev, errorMessage]);
      
      setTimeout(() => {
        setFileUploadProgress(null);
      }, 3000);
      
      return null;
    }
  };

  // Function to poll file processing status
  const filePollingRefs = useRef({});

  const startFileStatusPolling = useCallback((fileId) => {
    if (filePollingRefs.current[fileId]) {
      clearInterval(filePollingRefs.current[fileId]);
    }
    
    // Poll every 2 seconds for status updates
    filePollingRefs.current[fileId] = setInterval(async () => {
      try {
        const files = await listFiles(chatId);
        const fileUpdate = files.find(f => f.id === fileId);
        
        if (fileUpdate) {
          // Update file status in state
          setUploadedFiles(prev => prev.map(file => 
            file.id === fileId ? {...file, ...fileUpdate} : file
          ));
          
          // If finished processing or error, stop polling
          if (fileUpdate.process_status === 'upload_complete' || fileUpdate.process_status === 'error') {
            clearInterval(filePollingRefs.current[fileId]);
            delete filePollingRefs.current[fileId];
            
            // If completed, show a system message
            if (fileUpdate.process_status === 'upload_complete') {
              setMessages(prev => [...prev, {
                id: `system-file-complete-${Date.now()}`,
                sender: 'server',
                text: `File "${fileUpdate.filename}" has been processed and is now available for the assistant.`,
                timestamp: new Date().toISOString(),
                is_system_message: true
              }]);
            } else if (fileUpdate.process_status === 'error') {
              // If error, show an error message
              setMessages(prev => [...prev, {
                id: `system-file-error-${Date.now()}`,
                sender: 'server',
                text: `Error processing file "${fileUpdate.filename}". The file content may not be available to the assistant.`,
                timestamp: new Date().toISOString(),
                is_system_message: true,
                error: true
              }]);
            }
          }
        }
      } catch (error) {
        console.error('Error polling file status:', error);
      }
    }, 2000);
    
    // Clean up on unmount
    return () => {
      if (filePollingRefs.current[fileId]) {
        clearInterval(filePollingRefs.current[fileId]);
        delete filePollingRefs.current[fileId];
      }
    };
  }, [chatId]);

  // Clean up all polling intervals when component unmounts
  useEffect(() => {
    return () => {
      Object.keys(filePollingRefs.current).forEach(key => {
        clearInterval(filePollingRefs.current[key]);
      });
      filePollingRefs.current = {};
    };
  }, []);

  // Load files when chatId changes
  useEffect(() => {
    if (chatId) {
      loadChatFiles();
    }
  }, [chatId]);

  // Load files for the current chat
  const loadChatFiles = async () => {
    if (!chatId) return;
    
    try {
      const files = await listFiles(chatId);
      setUploadedFiles(files);
    } catch (error) {
      console.error('Error loading chat files:', error);
    }
  };

  // Delete a file
  const handleDeleteFile = async (fileId) => {
    try {
      await deleteFile(fileId);
      setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
      
      // Send websocket message about deleted file
      if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
        const message = {
          type: 'file_deleted',
          chat_id: chatId,
          file_id: fileId
        };
        websocketRef.current.send(JSON.stringify(message));
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting file:', error);
      return false;
    }
  };

  return {
    isAuthenticated,
    isConnected,
    chatId,
    setChatId,
    messages,
    setMessages,
    isLoading,
    setIsLoading,
    error,
    isReconnecting,
    reconnectCountdown,
    reconnectAttempts: reconnectAttempts.current,
    MAX_RECONNECT_ATTEMPTS,
    handleManualReconnect,
    handleSend,
    handleResendLastMessage,
    initializeWebSocket,
    cleanup,
    websocket: websocketRef.current,
    uploadedFiles,
    fileUploadProgress,
    handleFileUpload,
    handleDeleteFile
  };
};

export default useChatWebSocket;

export const sendMessage = (socket, message, config = {}) => {
  if (!socket || socket.readyState !== WebSocket.OPEN) {
    console.error('WebSocket is not connected');
    return false;
  }

  try {
    // Regular message
    if (typeof message === 'string') {
      const messageObj = {
        type: 'chat_message',
        message,
        ...config
      };
      socket.send(JSON.stringify(messageObj));
      return true;
    }
    
    // Object with type (for system messages like clear_chat_history)
    if (typeof message === 'object' && message.type) {
      socket.send(JSON.stringify(message));
      return true;
    }

    // Default case
    socket.send(JSON.stringify(message));
    return true;
  } catch (error) {
    console.error('Error sending message:', error);
    return false;
  }
};
